import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import './Contact.css';
import { ContactFormData } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

const initialForm: ContactFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  service: '',
  propertyType: '',
  message: '',
};

const Contact: React.FC = () => {
  const [form, setForm] = useState<ContactFormData>(initialForm);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName || !form.email) {
      setErrorMsg('Please fill in your name and email.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('success');
        setForm(initialForm);
      } else {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try calling us directly at (346) 419-4499.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Could not reach our server. Please call us at (346) 419-4499 or email paceplanllcgroup@gmail.com.');
    }
  };

  return (
    <div className="contact-page">
      {/* HERO */}
      <section className="page-hero section--dark">
        <div className="container-xl">
          <span className="section-label">Get In Touch</span>
          <h1 className="section-title section-title--white">Request a Free Quote</h1>
          <p className="section-sub section-sub--white" style={{ marginTop: '1rem' }}>
            Tell us about your property. We'll get back to you within 24 hours with an assessment and clear pricing — no obligation.
          </p>
        </div>
      </section>
      <div className="red-bar" />

      <section className="section">
        <div className="container-xl">
          <div className="row g-5 align-items-start">
            {/* INFO COLUMN */}
            <div className="col-12 col-lg-5 contact-info">
              <h2 className="section-title section-title--navy" style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', marginBottom: '0.5rem' }}>
                We Make It Easy
              </h2>
              <p className="contact-info__intro">
                Free on-site assessment. Clear scope. Upfront pricing. No pressure. We serve commercial and residential clients across the Greater Houston area — from single lots to multi-site portfolios.
              </p>

              <div className="contact-details">
                {[
                  {
                    icon: <svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.22 1.18 2 2 0 012.22 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>,
                    label: 'Phone',
                    value: '(346) 419-4499',
                    href: 'tel:3464194499',
                  },
                  {
                    icon: <svg viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
                    label: 'Email',
                    value: 'paceplanllcgroup@gmail.com',
                    href: 'mailto:paceplanllcgroup@gmail.com',
                  },
                  {
                    icon: <svg viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
                    label: 'Address',
                    value: '12335 Kingsride Ln #319\nHouston, TX 77042',
                    href: 'https://maps.google.com/?q=12335+Kingsride+Lane+Houston+TX+77042',
                  },
                  {
                    icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>,
                    label: 'Hours',
                    value: 'Mon–Sat: 7am–7pm\nEmergency scheduling available',
                    href: undefined,
                  },
                ].map((d, i) => (
                  <div className="contact-detail" key={i}>
                    <div className="contact-detail__icon">{d.icon}</div>
                    <div>
                      <span className="contact-detail__label">{d.label}</span>
                      {d.href
                        ? <a href={d.href} className="contact-detail__value">{d.value}</a>
                        : <span className="contact-detail__value">{d.value}</span>
                      }
                    </div>
                  </div>
                ))}
              </div>

              <div className="contact-certs">
                <div className="cert-pill">★ SBA Certified SDVOSB</div>
                <div className="cert-pill">★ SBA Certified VOSB</div>
              </div>

              <div className="service-area">
                <h4>Service Area</h4>
                <p>Greater Houston — Harris, Fort Bend, Montgomery, Brazoria, and Galveston Counties</p>
              </div>
            </div>

            {/* FORM COLUMN */}
            <div className="col-12 col-lg-7">
              <div className="contact-form-wrap">
                {status === 'success' ? (
                  <div className="form-success">
                    <div className="form-success__icon">✓</div>
                    <h3>Request Received!</h3>
                    <p>Thank you for reaching out. We'll review your request and get back to you within 24 hours with your free quote. If it's urgent, call us directly at <a href="tel:3464194499">(346) 419-4499</a>.</p>
                    <button className="btn btn-brand-navy mt-4" onClick={() => setStatus('idle')}>
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form className="contact-form" onSubmit={handleSubmit} noValidate>
                    <h3>Tell Us About Your Property</h3>

                    <div className="row g-3">
                      <Form.Group className="col-12 col-md-6">
                        <Form.Label htmlFor="firstName">First Name *</Form.Label>
                        <Form.Control id="firstName" name="firstName" type="text" placeholder="John"
                          value={form.firstName} onChange={handleChange} required />
                      </Form.Group>
                      <Form.Group className="col-12 col-md-6">
                        <Form.Label htmlFor="lastName">Last Name</Form.Label>
                        <Form.Control id="lastName" name="lastName" type="text" placeholder="Smith"
                          value={form.lastName} onChange={handleChange} />
                      </Form.Group>
                    </div>

                    <div className="row g-3 mt-0">
                      <Form.Group className="col-12 col-md-6">
                        <Form.Label htmlFor="email">Email Address *</Form.Label>
                        <Form.Control id="email" name="email" type="email" placeholder="john@company.com"
                          value={form.email} onChange={handleChange} required />
                      </Form.Group>
                      <Form.Group className="col-12 col-md-6">
                        <Form.Label htmlFor="phone">Phone Number</Form.Label>
                        <Form.Control id="phone" name="phone" type="tel" placeholder="(713) 555-0100"
                          value={form.phone} onChange={handleChange} />
                      </Form.Group>
                    </div>

                    <Form.Group className="mt-3">
                      <Form.Label htmlFor="company">Company / Property Name</Form.Label>
                      <Form.Control id="company" name="company" type="text" placeholder="Westpark Properties LLC"
                        value={form.company} onChange={handleChange} />
                    </Form.Group>

                    <div className="row g-3 mt-0">
                      <Form.Group className="col-12 col-md-6">
                        <Form.Label htmlFor="service">Service Needed</Form.Label>
                        <Form.Select id="service" name="service" value={form.service} onChange={handleChange}>
                          <option value="">Select a service...</option>
                          <option>Parking Lot Striping</option>
                          <option>ADA / TAS Compliance</option>
                          <option>Pressure Washing — Commercial</option>
                          <option>Pressure Washing — Residential</option>
                          <option>Soft Wash</option>
                          <option>Junk Removal</option>
                          <option>Dumpster / Can Cleanup</option>
                          <option>Full Property Service (Multiple)</option>
                          <option>Not Sure — Need Assessment</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="col-12 col-md-6">
                        <Form.Label htmlFor="propertyType">Property Type</Form.Label>
                        <Form.Select id="propertyType" name="propertyType" value={form.propertyType} onChange={handleChange}>
                          <option value="">Select type...</option>
                          <option>Strip Center / Retail Plaza</option>
                          <option>Office Park</option>
                          <option>Industrial / Warehouse</option>
                          <option>Medical / Dental</option>
                          <option>Restaurant / Food Service</option>
                          <option>Multi-Family / Apartment</option>
                          <option>Church / School / Daycare</option>
                          <option>Single-Family Residential</option>
                          <option>Other Commercial</option>
                        </Form.Select>
                      </Form.Group>
                    </div>

                    <Form.Group className="mt-3">
                      <Form.Label htmlFor="message">Describe Your Property & What You Need</Form.Label>
                      <Form.Control as="textarea" id="message" name="message"
                        placeholder="Tell us your address, property size, current condition, and what you're looking to get done. The more detail, the faster we can quote you..."
                        value={form.message} onChange={handleChange} rows={5} />
                    </Form.Group>

                    {errorMsg && <div className="form-error mt-3">{errorMsg}</div>}

                    <button
                      type="submit"
                      className="btn btn-brand-primary w-100 mt-3 py-3"
                      disabled={status === 'submitting'}
                    >
                      {status === 'submitting' ? 'Sending...' : 'Send My Request →'}
                    </button>

                    <p className="form-disclaimer mt-2">
                      We respond within 24 hours. First quote is always free. Your information is never shared.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
